/* eslint-disable react/no-unescaped-entities */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGamepad, FaVrCardboard, FaUnity, FaCheckCircle, FaCode } from 'react-icons/fa';
import { SiUnrealengine, SiBlender } from 'react-icons/si';
import { GiSpaceship, GiPistolGun, GiSwordman, GiPlatform } from 'react-icons/gi';
import { IoMdRocket } from 'react-icons/io';

const GamingShowcase = () => {
    const gamingServices = [
        {
            id: 'game-dev',
            title: '2D/3D Game Development',
            icon: <FaGamepad className="text-4xl text-green-400" />,
            description: 'Immersive gaming experiences crafted with cutting-edge technology',
            features: [
                'Cross-platform game development',
                'High-performance game engines',
                'Realistic physics and animations',
                'Multiplayer integration',
                'In-game monetization strategies'
            ],
            technologies: ['Unity', 'Unreal Engine', 'Godot', 'C#', 'C++'],
            examples: ['Mobile games', 'PC/Console games', 'Browser games']
        },
        {
            id: 'ar-vr',
            title: 'AR/VR Development',
            icon: <FaVrCardboard className="text-4xl text-purple-400" />,
            description: 'Next-level immersive experiences that blur reality',
            features: [
                'Virtual reality simulations',
                'Augmented reality applications',
                'Mixed reality experiences',
                '360° video integration',
                'Haptic feedback systems'
            ],
            technologies: ['Oculus SDK', 'ARKit', 'ARCore', 'OpenXR', 'WebXR'],
            examples: ['VR training', 'AR marketing', 'Virtual tours']
        },
        {
            id: 'metaverse',
            title: 'Metaverse Development',
            icon: <GiSpaceship className="text-4xl text-blue-400" />,
            description: 'Build your own persistent virtual worlds',
            features: [
                '3D virtual environments',
                'Avatar systems',
                'Virtual economies',
                'Social interaction systems',
                'Blockchain integration'
            ],
            technologies: ['WebGL', 'Three.js', 'Decentraland SDK', 'NFTs', 'Web3.js'],
            examples: ['Virtual events', 'Digital marketplaces', 'Social platforms']
        }
    ];

    const gameGenres = [
        { name: 'Action', icon: <GiPistolGun />, color: 'text-red-500' },
        { name: 'Adventure', icon: <GiSwordman />, color: 'text-yellow-500' },
        { name: 'Puzzle', icon: <FaUnity />, color: 'text-blue-500' },
        { name: 'Simulation', icon: <GiPlatform />, color: 'text-green-500' },
        { name: 'Racing', icon: <IoMdRocket />, color: 'text-purple-500' }
    ];

    const [activeService, setActiveService] = useState(0);
    const [showDemo, setShowDemo] = useState(false);

    return (
        <section className="relative bg-gray-900 text-white py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden opacity-20">
                {[...Array(20)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="absolute rounded-full bg-blue-500"
                        style={{
                            width: `${Math.random() * 10 + 5}px`,
                            height: `${Math.random() * 10 + 5}px`,
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`
                        }}
                        animate={{
                            y: [0, (Math.random() - 0.5) * 100],
                            x: [0, (Math.random() - 0.5) * 100],
                            opacity: [0.2, 0.8, 0.2]
                        }}
                        transition={{
                            duration: Math.random() * 10 + 10,
                            repeat: Infinity,
                            repeatType: 'reverse'
                        }}
                    />
                ))}
            </div>

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Hero Section */}
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-20"
                >
                    <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6">
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-500">
                            Game Development
                        </span>{' '}
                        <br />
                        That Captivates
                    </h1>
                    <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
                        We create immersive gaming experiences that players can't put down
                    </p>
                    {/* <button
                        onClick={() => setShowDemo(true)}
                        className="px-8 py-4 bg-gradient-to-r from-green-500 to-blue-600 rounded-full font-bold text-lg hover:shadow-xl hover:shadow-green-500/20 transition-all"
                    >
                        See Our Game Demo
                    </button> */}
                </motion.div>

                {/* Services Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
                    {gamingServices.map((service, index) => (
                        <motion.div
                            key={service.id}
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ delay: index * 0.1, duration: 0.5 }}
                            className={`bg-gray-800/50 backdrop-blur-sm rounded-xl overflow-hidden border-b-4 ${index === 0 ? 'border-green-500' : index === 1 ? 'border-purple-500' : 'border-blue-500'}`}
                            onClick={() => setActiveService(index)}
                        >
                            <div className="p-8 h-full flex flex-col">
                                <div className="mb-6">
                                    {service.icon}
                                </div>
                                <h2 className="text-2xl font-bold mb-3">{service.title}</h2>
                                <p className="text-gray-300 mb-6">{service.description}</p>
                                <div className="mt-auto">
                                    <div className="flex flex-wrap gap-2 mb-4">
                                        {service.technologies.map((tech, i) => (
                                            <span key={i} className="bg-gray-700/50 px-3 py-1 rounded-full text-sm">
                                                {tech}
                                            </span>
                                        ))}
                                    </div>
                                    {/* <button className="w-full py-3 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium transition-all">
                                        Learn More
                                    </button> */}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {/* Detailed Service View */}
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl overflow-hidden border border-gray-700 mb-20"
                >
                    <div className="p-8 md:p-12">
                        <div className="flex flex-col md:flex-row gap-8">
                            <div className="md:w-2/3">
                                <h2 className="text-3xl font-bold mb-6 flex items-center gap-4">
                                    {gamingServices[activeService].icon}
                                    {gamingServices[activeService].title}
                                </h2>
                                <p className="text-xl text-gray-300 mb-8">{gamingServices[activeService].description}</p>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                                    {gamingServices[activeService].features.map((feature, i) => (
                                        <div key={i} className="flex items-start gap-3 bg-gray-800/50 p-4 rounded-lg">
                                            <div className="text-green-400 mt-1">
                                                <FaCheckCircle />
                                            </div>
                                            <p className="text-gray-300">{feature}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="md:w-1/3">
                                <div className="bg-gray-800/50 rounded-lg p-6 sticky top-6">
                                    <h3 className="font-bold text-xl mb-4">Game Genres We Cover</h3>
                                    <div className="space-y-3 mb-6">
                                        {gameGenres.map((genre, i) => (
                                            <div key={i} className="flex items-center gap-3">
                                                <div className={`text-xl ${genre.color}`}>{genre.icon}</div>
                                                <span>{genre.name}</span>
                                            </div>
                                        ))}
                                    </div>

                                    <h3 className="font-bold text-xl mb-4">Example Projects</h3>
                                    <ul className="space-y-2 mb-8">
                                        {gamingServices[activeService].examples.map((example, i) => (
                                            <li key={i} className="flex items-center gap-2">
                                                <span className="text-blue-400">•</span>
                                                <span>{example}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <a href="/quickenquiry"><button className="w-full py-3 bg-gradient-to-r from-green-500 to-blue-600 rounded-lg font-bold hover:shadow-lg transition-all">
                                        Start Your Game Project
                                    </button></a>

                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Technology Showcase */}
                <div className="mb-20">
                    <h2 className="text-3xl font-bold text-center mb-12">
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-500">
                            Powerful Game Engines
                        </span>{' '}
                        We Master
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            { name: 'Unity', icon: <FaUnity className="text-5xl" />, color: 'text-gray-300' },
                            // { name: 'Unreal Engine', icon: <SiUnrealengine className="text-5xl" />, color: 'text-red-400' },
                            { name: 'Blender', icon: <SiBlender className="text-5xl" />, color: 'text-orange-400' },
                            { name: 'Custom Engines', icon: <FaCode className="text-5xl" />, color: 'text-blue-400' }
                        ].map((engine, i) => (
                            <motion.div
                                key={i}
                                whileHover={{ y: -10 }}
                                className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-8 text-center border border-gray-700"
                            >
                                <div className={`mb-4 ${engine.color}`}>{engine.icon}</div>
                                <h3 className="text-xl font-bold">{engine.name}</h3>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Call to Action */}
                <div className="text-center bg-gradient-to-r from-green-500/10 to-blue-600/10 rounded-xl p-12 border border-gray-700">
                    <h2 className="text-3xl md:text-4xl font-bold mb-6">
                        Ready to Create Your <span className="text-green-400">Game Masterpiece</span>?
                    </h2>
                    <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
                        Our team of passionate game developers will bring your vision to life with cutting-edge technology and creative gameplay design.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <a
                            href="/quickenquiry"
                            className="inline-block px-8 py-4 bg-gradient-to-r from-green-500 to-blue-600 rounded-full font-bold text-white hover:shadow-xl hover:shadow-green-500/20 transition-all text-center"
                        >
                            Get a Free Consultation
                        </a>

                        <a
                            href="/portfolio"
                            className="inline-block px-8 py-4 bg-transparent border-2 border-green-500 text-green-400 rounded-full font-bold hover:bg-green-500/10 transition-all text-center"
                        >
                            See Work
                        </a>
                    </div>
                </div>
            </div>

            {/* Demo Modal */}
            <AnimatePresence>
                {showDemo && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
                        onClick={() => setShowDemo(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="relative bg-gray-900 rounded-xl w-full max-w-6xl h-[80vh] overflow-hidden border border-gray-700"
                            onClick={e => e.stopPropagation()}
                        >
                            <button
                                onClick={() => setShowDemo(false)}
                                className="absolute top-4 right-4 z-10 bg-gray-800 p-2 rounded-full hover:bg-gray-700 transition-all"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>

                            <div className="h-full flex items-center justify-center">
                                <div className="text-center p-8">
                                    <FaGamepad className="text-6xl text-green-400 mx-auto mb-6" />
                                    <h3 className="text-2xl font-bold mb-4">Game Demo Coming Soon!</h3>
                                    <p className="text-gray-300 mb-6">We're preparing an interactive demo of our game development capabilities.</p>
                                    <p className="text-gray-400">In the meantime, check out our portfolio or contact us for specific examples.</p>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};

export default GamingShowcase;